import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext, useState } from 'react';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LeadButton from '@/components/LeadButton';
import { CaseCard, MobileCaseCard } from '@/components/WukongReviews/CaseCard';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

import { studentCases } from '../data';

import 'swiper/css';
import 'swiper/css/pagination';

// 自定义 Swiper pagination 样式
const swiperStyles = `
  .case-swiper .swiper-pagination-bullet {
    background: #C6C7D2;
    opacity: 1;
  }
  .case-swiper .swiper-pagination-bullet-active {
    background: #0a0a0a;
  }
`;

/**
 * 案例区模块
 * 展示真实学生故事
 */
const CaseSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);
  const [visibleCount, setVisibleCount] = useState(2);

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-详细案例',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  // 注入自定义样式
  React.useEffect(() => {
    const styleId = 'case-swiper-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = swiperStyles;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // 点击 Explore more 展开更多
  const handleExploreMore = () => {
    // 详细案例更多按钮加载埋点
    traceEvent({
      data: {
        _event: 'C_WallOfLove_StroyMore',
      },
    });

    if (visibleCount === 2) {
      setVisibleCount(4); // 第一次点击：显示4个
    } else if (visibleCount === 4) {
      setVisibleCount(5); // 第二次点击：显示全部5个
    }
  };

  // 是否显示 Explore more 按钮
  const showExploreMore = visibleCount < studentCases.length;

  return (
    <section className="flex w-full flex-col items-center px-4 py-[50px] lg:px-8 lg:py-[70px] xl:py-[90px]">
      {/* 标题 */}
      <h2
        className={`mb-6 text-center text-[28px] font-black leading-normal text-text-primary lg:mb-12 lg:mt-6 lg:text-[34px] xl:mb-15 xl:mt-8 xl:text-[48px] ${isZh ? 'font-sourceHanSans' : ''}`}>
        <span
          className="[&>span]:text-brand-red-100"
          dangerouslySetInnerHTML={{ __html: t('案例-标题') }}
        />
        <span className="hidden lg:inline"> ❤️</span>
      </h2>

      {/* PC 端 - 卡片列表 */}
      <div className="hidden w-full max-w-[984px] flex-col lg:flex lg:space-y-8 xl:max-w-[1320px] xl:space-y-10">
        {studentCases.slice(0, visibleCount).map(caseItem => (
          <CaseCard
            key={caseItem.id}
            caseId={caseItem.id}
            image={caseItem.image}
            rating={caseItem.rating}
          />
        ))}
      </div>

      {/* PC 端 - Explore more 按钮 */}
      {showExploreMore && (
        <button
          onClick={handleExploreMore}
          className="mt-8 hidden items-center space-x-2 text-[16px] font-medium text-text-secondary_1 transition-colors hover:text-brand-red-100 lg:flex xl:text-[18px]">
          <span>{t('案例-展开按钮')}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="size-5 xl:size-6">
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* 移动端 - Swiper 轮播 */}
      <div className="w-full lg:hidden">
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          slidesPerView={1}
          spaceBetween={0}
          className="case-swiper !pb-10">
          {studentCases.map(caseItem => (
            <SwiperSlide key={caseItem.id} className="!h-auto px-px">
              <MobileCaseCard
                caseId={caseItem.id}
                image={caseItem.image}
                rating={caseItem.rating}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* CTA 按钮 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace} className="mt-6 lg:mt-12 xl:mt-15">
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_CaseSection_CTAClick">
          <Button
            size="large"
            className="h-[52px] px-[30px] text-[20px] font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('案例-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default CaseSection;
